import { ReactElement } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { ImageUpload, Input, Typography } from '@weareopenr/component-library';
import { IRange } from 'src/api/getRanges';
import { IMenuLabel } from 'src/api/getMenuLabels';
import { MenuLabelsField } from 'src/components/MenuLabelsField';
import { IFormData } from 'src/components/MenuModal/types';
import {
  IProductRangeSelectorLabels,
  ProductRangeSelector,
} from 'src/components/ProductRangeSelector/ProductRangeSelector';
import { IMenuSetSourceLabels, MenuSetSourceField } from './MenuSetSourceField';
import styles from './MenuDetailsFields.module.scss';

export interface IMenuDetailsFieldsLabels {
  name: string;
  nameRequired: string;
  consumerName: string;
  consumerNameHelperText?: string;
  consumerNameNote?: string;
  image: string;
  sourceTitle?: string;
  source?: IProductRangeSelectorLabels;
  menuSetSource?: IMenuSetSourceLabels;
}

interface IMenuDetailsFieldsProps {
  form: UseFormReturn<IFormData>;
  labels: IMenuDetailsFieldsLabels;
  /** Omit `setImage` to leave the image out, e.g. where the form renders its own image field. */
  image?: File | string | null;
  setImage?: (image: File | string | null) => void;
  showSource: boolean;
  ranges?: IRange[];
  /** Shows the menu set's source options (All Menus, menu labels, ranges) in place of the menu's. */
  menuSetSource?: { showRanges: boolean };
  showMenuLabels: boolean;
  /** The brand's menu labels to pick from. */
  menuLabels?: IMenuLabel[];
  isMenuLabelsLoading?: boolean;
  /** Extra name check on top of required, e.g. uniqueness; return an error message to fail. */
  validateName?: (name: string) => true | string;
}

/**
 * The details fields shared by the menu and menu set forms: internal name, consumer name, menu labels,
 * menu source and image.
 */
export const MenuDetailsFields = ({
  form,
  labels,
  image,
  setImage,
  showSource,
  ranges,
  menuSetSource,
  showMenuLabels,
  menuLabels,
  isMenuLabelsLoading,
  validateName,
}: IMenuDetailsFieldsProps): ReactElement => {
  const {
    register,
    watch,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = form;

  return (
    <>
      <Input
        type="text"
        label={labels.name}
        hasMargin
        register={register('name', {
          required: labels.nameRequired,
          validate: {
            notEmpty: (value) => value.trim().length > 0 || labels.nameRequired,
            ...(validateName && { validateName }),
          },
          onChange: () => errors.name && clearErrors('name'),
        })}
        error={errors?.name?.message}
      />
      <Input
        type="text"
        label={labels.consumerName}
        hasMargin={!labels.consumerNameNote}
        helperText={labels.consumerNameHelperText}
        register={register('consumerName')}
      />
      {labels.consumerNameNote && (
        <Typography size="small" className={styles['consumer-name-note']}>
          {labels.consumerNameNote}
        </Typography>
      )}

      {showMenuLabels && (
        <div className={styles['menu-labels']}>
          <Controller
            control={control}
            name="menuLabels"
            render={({ field: { value, onChange } }) => (
              <MenuLabelsField
                menuLabels={menuLabels}
                value={value ?? []}
                onChange={onChange}
                loading={isMenuLabelsLoading}
              />
            )}
          />
        </div>
      )}

      {showSource && (
        <div className={labels.sourceTitle ? styles['source'] : undefined}>
          {labels.sourceTitle && (
            <Typography size="medium" weight="semi-bold">
              {labels.sourceTitle}
            </Typography>
          )}
          {menuSetSource && labels.menuSetSource ? (
            <MenuSetSourceField
              form={form}
              labels={labels.menuSetSource}
              menuLabels={menuLabels}
              showRanges={menuSetSource.showRanges}
              ranges={ranges}
            />
          ) : (
            <ProductRangeSelector
              ranges={ranges}
              register={register}
              clearErrors={clearErrors}
              errors={errors}
              setValue={setValue}
              watch={watch}
              productType="BUNDLE"
              control={control}
              labels={labels.source}
            />
          )}
        </div>
      )}

      {setImage && (
        <ImageUpload
          title={labels.image}
          prompt="Upload Image"
          image={image ?? null}
          shouldDeleteImage={false}
          setImage={setImage}
          uploadType="image"
        />
      )}
    </>
  );
};

export default MenuDetailsFields;
